import type { CSSProperties } from 'react';

/**
 * Shop appearance tokens — each maps to a CSS variable (--color-*) consumed by
 * the Tailwind palette (see tailwind.config.js / globals.css). The shop layout
 * overrides them on its root wrapper, so the admin app keeps the default theme.
 */
export const THEME_TOKENS = [
  'cream', 'linen', 'wheat', 'wood', 'bark', 'moss', 'moss-dark',
  'rust', 'rust-dark', 'ochre', 'sage', 'ink', 'sky', 'sky-dark',
] as const;

export type ThemeToken = (typeof THEME_TOKENS)[number];

export type ThemePreset = 'rustic' | 'minimal' | 'ocean' | 'forest';

export const THEME_PRESETS: Record<ThemePreset, Record<ThemeToken, string>> = {
  // matches globals.css defaults — the current look
  rustic: {
    cream: '#f7f2e9', linen: '#e8dcc8', wheat: '#e2d3ad', wood: '#8a6d4b',
    bark: '#5c4433', moss: '#5c7a52', 'moss-dark': '#3f5738',
    rust: '#a3503a', 'rust-dark': '#7a3a28', ochre: '#c89544',
    sage: '#8a9a7b', ink: '#3a332c', sky: '#4a78a8', 'sky-dark': '#2f5780',
  },
  minimal: {
    cream: '#ffffff', linen: '#f5f5f4', wheat: '#e5e5e4', wood: '#78716c',
    bark: '#292524', moss: '#44403c', 'moss-dark': '#1c1917',
    rust: '#b91c1c', 'rust-dark': '#7f1d1d', ochre: '#d97706',
    sage: '#a8a29e', ink: '#1c1917', sky: '#0369a1', 'sky-dark': '#0c4a6e',
  },
  ocean: {
    cream: '#f0f7fa', linen: '#e1eef4', wheat: '#c9dfe9', wood: '#5b7d92',
    bark: '#1e3a4c', moss: '#2c7a8c', 'moss-dark': '#1b5563',
    rust: '#c2604a', 'rust-dark': '#93402f', ochre: '#d99a4e',
    sage: '#7ea3ad', ink: '#1f2e38', sky: '#3a8fc2', 'sky-dark': '#256b94',
  },
  forest: {
    cream: '#f2f5ee', linen: '#e3ead9', wheat: '#cdd8bb', wood: '#77835f',
    bark: '#35402a', moss: '#5f7f3f', 'moss-dark': '#42592c',
    rust: '#a05a3a', 'rust-dark': '#76402a', ochre: '#c49a3f',
    sage: '#93a57a', ink: '#2f3327', sky: '#5a87a0', 'sky-dark': '#3d6378',
  },
};

export const FONTS: Record<string, string> = {
  system: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, "Times New Roman", Times, serif',
  humanist: '"Trebuchet MS", "Segoe UI", Verdana, sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
};

export const RADII: Record<string, string> = {
  sharp: '0.25rem',
  default: '0.75rem',
  round: '1.5rem',
};

export type DesignConfig = {
  theme?: string;
  colors?: Partial<Record<ThemeToken, string>>;
  font?: string;
  radius?: string;
  logoAssetId?: number | null;
};

/** Effective token value: explicit override → preset → rustic default. */
export function resolveToken(design: DesignConfig | undefined, token: ThemeToken): string {
  return design?.colors?.[token]
    ?? THEME_PRESETS[(design?.theme as ThemePreset) || 'rustic']?.[token]
    ?? THEME_PRESETS.rustic[token];
}

/** Style props to apply on the shop root wrapper (CSS var overrides + font + radius). */
export function buildShopThemeStyle(design: DesignConfig | undefined): CSSProperties {
  const style: Record<string, string> = {};
  const preset: ThemePreset = design?.theme && design.theme in THEME_PRESETS
    ? (design.theme as ThemePreset) : 'rustic';
  for (const token of THEME_TOKENS) {
    const v = design?.colors?.[token] ?? THEME_PRESETS[preset][token];
    // skip vars identical to the global default — keeps :root clean for the default look
    if (v !== THEME_PRESETS.rustic[token]) style[`--color-${token}`] = v;
  }
  if (design?.radius && RADII[design.radius]) style['--radius-rustic'] = RADII[design.radius];
  const out = style as CSSProperties;
  if (design?.font && FONTS[design.font]) out.fontFamily = FONTS[design.font];
  return out;
}
